/**
 * CoPilot domain profile — how the CoPilot behaves in one domain (and, for a
 * domain kept per country, in one country).
 *
 * The profile is a BRAIN config (`copilot_domain_profile.<domain>`), written
 * in English (translated on demand), stored with its revisions, served only
 * when valid and only as deployed: it changes by pull request and deploy, never
 * by an edit through the API (R3 — every profile, Jose 16 Oct 2026).
 *
 * What each part does:
 * - `persona.extension`    — the domain's own instructions to the model (L5).
 * - `context.slices`       — domain data the platform adds to the turn's
 *                            grounding, quarantined like every data block.
 * - `tools`                — the tools the model may call here: the persona's
 *                            whole catalog, or only the ones named.
 * - `knowledge`            — `open`: the knowledge base for the user's persona
 *                            and country; `strict`: only notes written for this
 *                            domain and country, enforced by the server — with
 *                            no note, no content, the hand-off instead.
 * - `inputRules`           — intents recognised in the user's message in any of
 *                            the languages listed; a match adds a deterministic
 *                            directive to the turn.
 * - `outputValidator`      — patterns a reply must not contain, and an optional
 *                            model judge with the profile's rubric (each rule
 *                            judged against the user's message alone, or the
 *                            message and the turn's facts); a reply that
 *                            fails is written again once with its `repair`
 *                            directive (when given) and validated again; what
 *                            still fails is replaced by the hand-off, localized.
 * - `handoff`              — what the CoPilot says when it must not answer.
 * - `nextStep`             — `offer_card`: every answer in the domain ends with
 *                            the partner's offer card (only with `partnerRouting`).
 * - `partnerRouting`       — the exception to D11 (the Companion never routes to
 *                            a person): the partner this domain routes to, and
 *                            what it offers. Without it there is no offer card.
 * - `modelRoute`, `evalSuite` — the model route of the dialog and the golden
 *                            suite that proves the profile.
 */
import { COPILOT_PARTNER_OFFER_KINDS, type CoPilotPartnerOfferKind } from '@tonnie/types';
import { z } from 'zod';

import { COPILOT_DOMAINS, copilotDomainScope, type CoPilotDomain } from './domains';

/** The languages a pattern is written for — primary subtags of Tonnie's locales. */
export const COPILOT_PATTERN_LANGUAGES = ['en', 'pt', 'es', 'it'] as const;

/** Regular-expression sources per language, matched case-insensitively. */
const patternsByLanguage = z
  .partialRecord(z.enum(COPILOT_PATTERN_LANGUAGES), z.array(z.string().min(1)).min(1))
  .refine((patterns) => Object.keys(patterns).length > 0, 'names at least one language');

/** What a rule of the judge's rubric reads besides the reply — see `outputValidator.judge`. */
export const COPILOT_JUDGE_SOURCES = ['message', 'facts'] as const;
export type CoPilotJudgeSource = (typeof COPILOT_JUDGE_SOURCES)[number];

/** The domain data the platform can add to a turn's grounding. */
export const COPILOT_CONTEXT_SLICES = ['inout_status'] as const;
export type CoPilotContextSlice = (typeof COPILOT_CONTEXT_SLICES)[number];

/** The domain each context slice belongs to — a slice serves its own domain. */
const SLICE_DOMAIN: Record<CoPilotContextSlice, CoPilotDomain> = { inout_status: 'inout' };

/** What a partner can be routed to from the CoPilot — the offer card's kinds. */
export const COPILOT_PARTNER_OFFERS = COPILOT_PARTNER_OFFER_KINDS;
export type CoPilotPartnerOffer = CoPilotPartnerOfferKind;

/** The tool that ends a turn with the partner's offer card. */
export const OFFER_NEXT_STEP_TOOL = 'offer_next_step';

export const copilotDomainProfileSchema = z
  .object({
    domain: z.enum(COPILOT_DOMAINS),
    /** `global`, or the ISO-2 country of a profile kept per country. */
    market: z.string().regex(/^(global|[A-Z]{2})$/),
    version: z.string().min(1),
    persona: z.object({ extension: z.string().min(1) }).strict(),
    context: z.object({ slices: z.array(z.enum(COPILOT_CONTEXT_SLICES)) }).strict(),
    tools: z.discriminatedUnion('mode', [
      z.object({ mode: z.literal('persona_catalog') }).strict(),
      z
        .object({
          mode: z.literal('allowlist'),
          actionIds: z.array(z.string().regex(/^[a-z][a-z0-9_]*$/)).min(1),
        })
        .strict(),
    ]),
    knowledge: z.object({ mode: z.enum(['open', 'strict']) }).strict(),
    inputRules: z.array(
      z
        .object({
          id: z.string().regex(/^[A-Z][A-Z0-9_]*$/),
          patterns: patternsByLanguage,
          directive: z.string().min(1),
        })
        .strict()
    ),
    outputValidator: z
      .object({
        rules: z.array(
          z
            .object({ id: z.string().regex(/^[A-Z][A-Z0-9_]*$/), patterns: patternsByLanguage })
            .strict()
        ),
        judge: z
          .object({
            rubric: z
              .array(
                z
                  .object({
                    rule: z.string().min(1),
                    /**
                     * What the reply is judged against. `message`: the user's
                     * message alone — a rule about what the reply SAYS (a legal
                     * verdict), which the turn's facts must not colour: InOut's
                     * facts carry each limit's legal consequence, and a judge
                     * reading them took a plain count of days left for a
                     * verdict on the permit. `facts`: the message and the facts
                     * (tool results and domain data) — a rule about where the
                     * reply's numbers come from.
                     */
                    against: z.enum(COPILOT_JUDGE_SOURCES),
                  })
                  .strict()
              )
              .min(1),
          })
          .strict()
          .nullable(),
        /**
         * The directive a refused reply is written again with, once, before the
         * hand-off — so the answer the person asked for survives what the model
         * added and the profile forbids. Null: a refused reply is handed off.
         */
        repair: z.string().min(1).nullable().default(null),
      })
      .strict(),
    handoff: z.string().min(1).nullable(),
    nextStep: z.object({ mode: z.enum(['none', 'offer_card']) }).strict(),
    partnerRouting: z
      .object({
        partnerCode: z.string().regex(/^[A-Z][A-Z0-9_]*$/),
        offers: z.array(z.enum(COPILOT_PARTNER_OFFERS)).min(1),
      })
      .strict()
      .nullable(),
    modelRoute: z.literal('dialog'),
    evalSuite: z.string().min(1).nullable(),
  })
  .strict();

export type CoPilotDomainProfile = z.infer<typeof copilotDomainProfileSchema>;

/** The BRAIN config key of a domain's profile. */
export type CoPilotDomainProfileKey = `copilot_domain_profile.${CoPilotDomain}`;

export function copilotDomainProfileKey(domain: CoPilotDomain): CoPilotDomainProfileKey {
  return `copilot_domain_profile.${domain}`;
}

/** Whether a config key is a CoPilot domain's profile key. */
export function isCoPilotDomainProfileKey(key: string): key is CoPilotDomainProfileKey {
  return domainOfProfileKey(key) !== undefined;
}

/** The domain of a profile key, or undefined for any other config key. */
export function domainOfProfileKey(key: string): CoPilotDomain | undefined {
  const prefix = 'copilot_domain_profile.';
  if (!key.startsWith(prefix)) return undefined;
  const domain = key.slice(prefix.length);
  return (COPILOT_DOMAINS as readonly string[]).includes(domain)
    ? (domain as CoPilotDomain)
    : undefined;
}

function patternProblems(where: string, patterns: Record<string, string[] | undefined>): string[] {
  return Object.entries(patterns).flatMap(([language, sources]) =>
    (sources ?? []).flatMap((source) => {
      try {
        new RegExp(source, 'iu');
        return [];
      } catch {
        return [`${where} (${language}): "${source}" is not a valid pattern`];
      }
    })
  );
}

/**
 * What is wrong with a profile — its shape, and the rules no shape can say:
 * the offer card needs a declared partner route (R2), a profile that can
 * refuse needs its hand-off, the market matches the domain's scope, and every
 * pattern compiles. Empty when nothing is.
 */
export function copilotDomainProfileProblems(
  content: unknown,
  expected?: { domain: CoPilotDomain; market: string }
): string[] {
  const parsed = copilotDomainProfileSchema.safeParse(content);
  if (!parsed.success) {
    return parsed.error.issues.map(
      (issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`
    );
  }
  const profile = parsed.data;
  const problems: string[] = [];

  if (expected && profile.domain !== expected.domain) {
    problems.push(`domain is ${profile.domain}, stored as ${expected.domain}`);
  }
  if (expected && profile.market !== expected.market) {
    problems.push(`market is ${profile.market}, stored as ${expected.market}`);
  }
  const countryScoped = copilotDomainScope(profile.domain) === 'country';
  if (countryScoped && profile.market === 'global') {
    problems.push(`${profile.domain} is kept per country — its profile names a country`);
  }
  if (!countryScoped && profile.market !== 'global') {
    problems.push(`${profile.domain} is one profile for every country — its market is global`);
  }

  if (profile.nextStep.mode === 'offer_card' && !profile.partnerRouting) {
    problems.push(
      'nextStep offer_card needs partnerRouting — no declared route, no offer card (R2)'
    );
  }
  const offersTool =
    profile.tools.mode === 'allowlist' && profile.tools.actionIds.includes(OFFER_NEXT_STEP_TOOL);
  if (offersTool && !profile.partnerRouting) {
    problems.push(`${OFFER_NEXT_STEP_TOOL} needs partnerRouting (R2)`);
  }
  if (profile.partnerRouting && profile.nextStep.mode !== 'offer_card') {
    problems.push('partnerRouting is declared for the offer card — nextStep is offer_card');
  }

  const canRefuse =
    profile.knowledge.mode === 'strict' ||
    profile.outputValidator.rules.length > 0 ||
    profile.outputValidator.judge !== null;
  if (canRefuse && !profile.handoff) {
    problems.push('a profile that can refuse an answer has its handoff');
  }
  const validates =
    profile.outputValidator.rules.length > 0 || profile.outputValidator.judge !== null;
  if (profile.outputValidator.repair !== null && !validates) {
    problems.push('outputValidator.repair needs a rule or a judge — nothing would refuse a reply');
  }

  for (const slice of profile.context.slices) {
    if (SLICE_DOMAIN[slice] !== profile.domain) {
      problems.push(`context slice ${slice} belongs to ${SLICE_DOMAIN[slice]}`);
    }
  }

  const ids = [...profile.inputRules, ...profile.outputValidator.rules].map((rule) => rule.id);
  const repeated = ids.filter((id, i) => ids.indexOf(id) !== i);
  if (repeated.length > 0) problems.push(`rule ids repeat: ${[...new Set(repeated)].join(', ')}`);

  for (const rule of profile.inputRules) {
    problems.push(...patternProblems(`input rule ${rule.id}`, rule.patterns));
  }
  for (const rule of profile.outputValidator.rules) {
    problems.push(...patternProblems(`output rule ${rule.id}`, rule.patterns));
  }
  return problems;
}
